import * as React from "react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { Popover } from "@/components/ui";

import { useIsMobile } from "@/hooks/use-mobile";
import { Search, ShoppingCart, User, Menu, Home, Package, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StedIcon } from "@/assets/icons";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/providers/user";
import { useGetAllCategories, useGetCartCount, useGetCartItems } from "@/api/queries";
import { formatCurrency } from "@/lib/utils";

export function Navbar() {
  const isMobile = useIsMobile();

  const { data: cartItemsResponse, isFetching: isFetchingCartItems } = useGetCartItems();
  const cartItems = cartItemsResponse?.data?.items || [];
  const cartSummary = cartItemsResponse?.data?.summary;

  const navigate = useNavigate();
  const { user } = useUser("navbar");
  const isAuthenticated = !!user;
  const { data } = useGetCartCount();
  const { data: categoriesData } = useGetAllCategories();

  const categories = categoriesData?.data;
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Handle search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const term = searchInputRef.current?.value.trim();
    if (term) {
      navigate(`/shop?search=${encodeURIComponent(term)}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    sessionStorage.clear();

    // Use window.location.href for navigation + refresh in one go
    window.location.href = "/";
  };

  const DesktopNavbar = () => (
    <div className='sticky top-0 z-50 w-full shadow-md bg-background'>
      <div
        className='container mx-auto flex h-16 items-center justify-between px-4'
        style={{ position: "relative" }}
      >
        {/* Logo */}
        <div className='flex items-center'>
          <a href='/' className='flex items-center'>
            <StedIcon size='lg' />
          </a>
        </div>

        {/* Main Navigation */}
        <NavigationMenu className='hidden md:flex'>
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuLink className={navigationMenuTriggerStyle()} href='/'>
                Home
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuTrigger>Shop Now</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className='grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]'>
                  {categories?.slice(0, 6)?.map((category) => (
                    <ListItem
                      key={category.id}
                      title={category.name}
                      href={`/shop/category/${category.id}`}
                      className='group hover:text-white'
                    >
                      <p className='group-hover:text-white'>{category.description}</p>
                    </ListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* <NavigationMenuItem>
              <NavigationMenuLink className={navigationMenuTriggerStyle()} href='/about'>
                About Us
              </NavigationMenuLink>
            </NavigationMenuItem> */}

            <NavigationMenuItem>
              <NavigationMenuLink className={navigationMenuTriggerStyle()} href='/faq'>
                FAQ
              </NavigationMenuLink>
            </NavigationMenuItem>

            {/* <NavigationMenuItem>
              <NavigationMenuLink className={navigationMenuTriggerStyle()} href='/blog'>
                Blog
              </NavigationMenuLink>
            </NavigationMenuItem> */}

            {/* <NavigationMenuItem>
              <NavigationMenuLink className={navigationMenuTriggerStyle()} href='/categories'>
                Categories
              </NavigationMenuLink>
            </NavigationMenuItem> */}
          </NavigationMenuList>
        </NavigationMenu>

        {/* Search, Cart and Account */}
        <div className='flex items-center space-x-4'>
          {/* Search Bar */}
          <div className='relative hidden md:block'>
            <form onSubmit={handleSearch} className='flex items-center'>
              <div className='relative flex-grow'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-500 z-10' />
                <Input
                  placeholder='Search products...'
                  className='w-64 pl-10 pr-4 rounded-l-full bg-primary-foreground text-primary border-r-0'
                  ref={searchInputRef}
                  type='text'
                />
              </div>
              <Button type='submit' size='sm' className='h-10 rounded-l-none rounded-r-full'>
                Search
              </Button>
            </form>
          </div>

          {/* Cart Dropdown - Using hover behavior */}
          {isAuthenticated && (
            <Popover.Root>
              <Popover.Trigger asChild>
                <div className='relative cursor-pointer'>
                  <ShoppingCart className='h-6 w-6' />
                  <Badge className='absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full p-0'>
                    {data?.data?.count || 0}
                  </Badge>
                </div>
              </Popover.Trigger>

              <Popover.Content className='w-80 z-50'>
                <h3 className='text-lg font-semibold mb-2'>Your Cart ({data?.data?.count || 0})</h3>
                {isFetchingCartItems ? (
                  <div className='flex items-center justify-center py-8'>
                    <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500'></div>
                  </div>
                ) : cartItems.length === 0 ? (
                  <div className='text-center py-8 text-gray-500'>
                    <p>Your cart is empty</p>
                  </div>
                ) : (
                  <>
                    <div className='space-y-3 max-h-64 overflow-y-auto'>
                      {cartItems.map((item) => (
                        <div
                          key={item.id}
                          className='flex items-center space-x-3 border-b pb-3 pt-1'
                        >
                          <div className='h-12 w-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0'>
                            {item.product.images?.[0]?.url ? (
                              <img
                                src={item.product.images[0].url}
                                alt={item.product.images[0].altText || item.product.name}
                                className='max-h-10 max-w-10 rounded object-cover'
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = "/api/placeholder/40/40";
                                }}
                              />
                            ) : (
                              <Package className='h-6 w-6 text-gray-400' />
                            )}
                          </div>
                          <div className='min-w-0 flex-1'>
                            <p className='font-medium text-sm truncate'>{item.product.name}</p>
                            {item.variant && (
                              <p className='text-xs text-gray-500 truncate'>
                                {item.variant.name}: {item.variant.value}
                              </p>
                            )}
                            <div className='flex justify-between text-sm'>
                              <span className='text-gray-600'>
                                {item.quantity} × {formatCurrency(parseFloat(item.unitPrice))}
                              </span>
                              <span className='font-medium'>
                                {formatCurrency(parseFloat(item.totalPrice))}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className='mt-3 pt-2 border-t'>
                      <div className='flex justify-between mb-3'>
                        <span className='font-medium'>Subtotal:</span>
                        <span className='font-semibold'>
                          {formatCurrency(parseFloat(cartSummary?.subtotal || "0"))}
                        </span>
                      </div>
                      <div className='flex flex-col gap-2'>
                        <Button asChild className='w-full bg-blue-600 hover:bg-blue-700'>
                          <a href='/cart'>View Cart</a>
                        </Button>
                        <Button variant='outline' asChild className='w-full'>
                          <a href='/checkout'>Checkout</a>
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </Popover.Content>
            </Popover.Root>
          )}

          {/* Orders - Only show if authenticated */}
          {isAuthenticated && (
            <a href='/orders' className='hidden md:block'>
              <Package className='h-6 w-6' />
            </a>
          )}

          {/* User Menu - Hover Dropdown */}
          {isAuthenticated ? (
            <Popover.Root>
              <Popover.Trigger asChild>
                <div className='h-8 w-8 rounded-full bg-primary-foreground p-0 flex items-center justify-center cursor-pointer'>
                  <User className='h-5 w-5' />
                </div>
              </Popover.Trigger>

              <Popover.Content className='w-64 z-50'>
                <div className='p-4'>
                  <div className='flex items-center space-x-3 pb-3 border-b mb-2'>
                    <div className='h-10 w-10 flex items-center justify-center rounded-full bg-primary flex-shrink-0'>
                      <User className='h-6 w-6 text-primary-foreground' />
                    </div>
                    <div className='min-w-0'>
                      <p className='font-semibold truncate'>{user.fullName}</p>
                      <p className='text-xs text-muted-foreground truncate'>{user.email}</p>
                    </div>
                  </div>
                  <nav className='space-y-1'>
                    <ProfileLink href='/profile' label='My Profile' className='' />
                    <ProfileLink href='/profile/password' label='Change Password' className='' />
                    <ProfileLink href='/profile/addresses' label='Manage Addresses' className='' />
                    <ProfileLink href='/orders' label='Order History' className='' />
                    <ProfileLink href='/profile/wishlist' label='Wishlist' className='' />
                    <div className='pt-2 mt-2 border-t'>
                      <button
                        onClick={handleLogout}
                        className='block py-2 px-1 text-sm rounded hover:bg-accent hover:text-accent-foreground transition-colors text-red-500 w-full text-left'
                      >
                        Logout
                      </button>
                    </div>
                  </nav>
                </div>
              </Popover.Content>
            </Popover.Root>
          ) : (
            <div className='flex space-x-2'>
              <Button size='sm' asChild>
                <a href='/signup'>Register</a>
              </Button>
              <Button variant='outline' size='sm' asChild>
                <a href='/login'>Login</a>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const MobileHeader = () => (
    <div className='sticky top-0 z-50 bg-white flex h-14 w-full items-center justify-between  px-4 text-primary-foreground shadow-md md:hidden'>
      {/* Logo */}
      <a href='/' className='text-lg font-bold'>
        <StedIcon size='md' />
      </a>

      {/* Search and Menu */}
      <div className='flex items-center space-x-2'>
        <a
          onClick={(e) => {
            e.preventDefault();
            navigate("/shop");
          }}
          href='/shop'
        >
          <Search color='#000' className='h-5 w-5' />
        </a>
        <Sheet>
          <SheetTrigger asChild>
            <Button variant='ghost' size='icon' className='text-primary-foreground'>
              <Menu className='h-6 w-6' color='#000' />
            </Button>
          </SheetTrigger>
          <SheetContent side='right' className='overflow-y-auto'>
            <StedIcon size='md' />
            <div className='mt-6 flex flex-col space-y-6'>
              {/* <a href='/' className='flex items-center text-base font-medium'>
                Home
              </a>
              <a href='/about' className='flex items-center text-base font-medium'>
                About Us
              </a>

              <a href='/faq' className='flex items-center text-base font-medium'>
                FAQ
              </a> */}

              {/* Mobile Search */}
              {/* <form onSubmit={handleSearch} className='mt-2 mb-2'>
                <div className='flex items-center'>
                  <div className='relative flex-grow'>
                    <Input
                      placeholder='Search products...'
                      className='w-full pl-10 pr-2 rounded-l-md bg-primary-foreground border-r-0'
                      ref={searchInputRef}
                      type='text'
                    />
                  </div>
                  <Button type='submit' size='sm' className='h-10 rounded-l-none'>
                    Search
                  </Button>
                </div>
              </form> */}

              {/* Categories */}
              <div className='space-y-2'>
                <h3 className='text-lg font-semibold'>Shop By Category</h3>
                <div className='space-y-1 pl-1'>
                  {categories?.slice(0, 6)?.map((category) => (
                    <a
                      key={category.name}
                      href={`/shop/category/${category.id}`}
                      className='block py-1.5 text-sm hover:text-primary'
                    >
                      {category.name}
                    </a>
                  ))}
                </div>
              </div>

              <div className='space-y-2 mt-4'>
                <h3 className='text-lg font-semibold'>Quick Links</h3>
                <div className='space-y-1 pl-1'>
                  <a href='/about' className='block py-1.5 text-sm hover:text-primary'>
                    About Us
                  </a>
                  <a href='/faq' className='block py-1.5 text-sm hover:text-primary'>
                    FAQ
                  </a>
                  {/* <a href='/blog' className='block py-1.5 text-sm hover:text-primary'>
                    Blog
                  </a> */}
                  <a href='/categories' className='block py-1.5 text-sm hover:text-primary'>
                    Categories
                  </a>
                  <a href='/contact' className='block py-1.5 text-sm hover:text-primary'>
                    Contact Us
                  </a>
                </div>
              </div>

              {isAuthenticated ? (
                <div className='mt-4 flex flex-col space-y-4 rounded-lg bg-secondary/20 p-4'>
                  <div className='flex items-center space-x-3 pb-2 border-b'>
                    <div className='flex h-10 w-10 items-center justify-center rounded-full bg-primary'>
                      <User className='h-6 w-6 text-primary-foreground' />
                    </div>
                    <div>
                      <p className='font-semibold'>{user.fullName}</p>
                      <p className='text-sm text-muted-foreground'>{user.email}</p>
                    </div>
                  </div>
                  <a href='/profile' className='text-sm py-1'>
                    My Profile
                  </a>
                  <a href='/profile/password' className='text-sm py-1'>
                    Change Password
                  </a>
                  <a href='/profile/address' className='text-sm py-1'>
                    Manage Addresses
                  </a>
                  <a href='/orders' className='text-sm py-1'>
                    Order History
                  </a>
                  <a href='/profile/wishlist' className='text-sm py-1'>
                    Wishlist
                  </a>
                  <button onClick={handleLogout} className='text-sm py-1 text-red-500 text-left'>
                    Logout
                  </button>
                </div>
              ) : (
                <div className='flex flex-col space-y-2'>
                  <Button asChild>
                    <a href='/login'>Login</a>
                  </Button>
                  <Button variant='outline' asChild>
                    <a href='/register'>Register</a>
                  </Button>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );

  const MobileBottomNav = () => (
    <div className='fixed bottom-0 z-50 flex w-full items-center justify-around border-t bg-background py-2 md:hidden'>
      <a href='/' className='flex flex-col items-center p-2'>
        <Home className='h-5 w-5' />
        <span className='text-xs'>Home</span>
      </a>
      <a href='/shop' className='flex flex-col items-center p-2'>
        <Search className='h-5 w-5' />
        <span className='text-xs'>Shop</span>
      </a>
      {/* <a href='/blog' className='flex flex-col items-center p-2'>
        <Book className='h-5 w-5' />
        <span className='text-xs'>Blog</span>
      </a> */}
      <a href='/categories' className='flex flex-col items-center p-2'>
        <Layers className='h-5 w-5' />
        <span className='text-xs'>Categories</span>
      </a>
      <a href='/cart' className='flex flex-col items-center p-2'>
        <div className='relative'>
          <ShoppingCart className='h-5 w-5' />
          <Badge className='absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full p-0 text-xs'>
            {data?.data?.count || 0}
          </Badge>
        </div>
        <span className='text-xs'>Cart</span>
      </a>
      {isAuthenticated && (
        <a href='/orders' className='flex flex-col items-center p-2'>
          <Package className='h-5 w-5' />
          <span className='text-xs'>Orders</span>
        </a>
      )}
      <a href={isAuthenticated ? "/profile" : "/login"} className='flex flex-col items-center p-2'>
        <User className='h-5 w-5' />
        <span className='text-xs'>Account</span>
      </a>
    </div>
  );

  return (
    <>
      {isMobile ? <MobileHeader /> : <DesktopNavbar />}
      {isMobile && <MobileBottomNav />}
      {/* {isMobile && <div className='pb-16' />}  */}
    </>
  );
}

// Helper component for profile links with proper TypeScript types
interface ProfileLinkProps {
  href: string;
  label: string;
  className: string;
}

const ProfileLink: React.FC<ProfileLinkProps> = ({ href, label, className }) => (
  <a
    href={href}
    className={cn(
      "block py-2 px-1 text-sm rounded hover:bg-accent hover:text-accent-foreground transition-colors whitespace-nowrap overflow-hidden text-ellipsis",
      className,
    )}
  >
    {label}
  </a>
);

interface ListItemProps extends React.ComponentPropsWithoutRef<"a"> {
  title: string;
}

const ListItem = React.forwardRef<React.ElementRef<"a">, ListItemProps>(
  ({ className, title, children, ...props }, ref) => {
    return (
      <li>
        <NavigationMenuLink asChild>
          <a
            ref={ref}
            className={cn(
              "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
              className,
            )}
            {...props}
          >
            <div className='text-sm font-medium leading-none'>{title}</div>
            {children && (
              <p className='line-clamp-2 text-sm leading-snug text-muted-foreground'>{children}</p>
            )}
          </a>
        </NavigationMenuLink>
      </li>
    );
  },
);
ListItem.displayName = "ListItem";
